<template>
  <v-app-bar elevation="0" density="compact" app class="pa-2 border-b-thin px-2">
    <div v-if="!drawer" class="d-flex justify-space-between px-3 align-center">
      <div class="rounded-lg cursor-pointer bg-grey pa-1 mx-3" @click="$emit('toggle-sidebar')">
        <v-icon color="warning" size="30">
          {{!drawer ? "mdi-menu-right" : "mdi-menu-left" }}
        </v-icon>
      </div>
      <router-link to="/">
        <img contain height="32" src="/images/logo_main.png" />
      </router-link>
    </div>
    <div>
       <v-icon @click="$router.back()" color="warning" size="26">
          mdi-menu-left
        </v-icon>
        <span class="text-subtitle-1 font-weight-bold ml-2" style="color: #344054;">
          {{ $route.meta.title }}
        </span>
    </div>
    <v-row no-gutters>
      <v-col class="d-flex align-center justify-end">
        <v-menu v-model="notificationMenu" location="bottom end" offset="10" :close-on-content-click="false">
          <template #activator="{ props }">
            <div
              v-bind="props"
              class="notification-btn cursor-pointer mr-4 mt-2"
            >
              <v-badge
                :content="unreadNotifications"
                color="error"
                floating
                :model-value="unreadNotifications > 0"
              >
                <v-icon size="24">
                  mdi-bell-outline
                </v-icon>
              </v-badge>
            </div>
          </template>

          <!-- Notification Card -->
          <v-card
            width="390"
            rounded="lg"
            class="notification-card"
            elevation="8"
          >
            <!-- Notification List -->
            <div class="notification-list">
              <div
                v-for="notification in notifications"
                :key="notification.id"
                class="notification-item"
                :class="{ 'notification-unread': !notification.read }"
                @click="openNotification(notification)"
              >
                <!-- Icon -->
                <div
                  class="notification-icon"
                  :class="notification.type"
                >
                  <v-icon size="19">
                    {{ notification.icon }}
                  </v-icon>
                </div>

                <!-- Content -->
                <div class="notification-content">
                  <div class="d-flex justify-space-between align-start">
                    <h4>{{ notification.title }}</h4>

                    <span class="notification-time">
                      {{ notification.time }}
                    </span>
                  </div>

                  <p>
                    {{ notification.message }}
                  </p>

                  <span class="notification-reference">
                    {{ notification.reference }}
                  </span>
                </div>

                <!-- Unread Dot -->
                <span
                  v-if="!notification.read"
                  class="unread-dot"
                ></span>
              </div>

              <!-- Empty -->
              <div
                v-if="!notifications.length"
                class="notification-empty"
              >
                <v-icon size="35" color="grey">
                  mdi-bell-off-outline
                </v-icon>

                <p>No notifications</p>
              </div>
            </div>

            <v-divider />

            <!-- Footer -->
            <div class="notification-footer">
              <v-btn
                variant="text"
                block
                color="primary"
                @click="viewAllNotifications"
              >
                View all notifications
                <v-icon end size="18">
                  mdi-arrow-right
                </v-icon>
              </v-btn>
            </div>
          </v-card>
        </v-menu>
        <v-menu v-model="menu" offset-y transition="scale-transition" location="bottom">
          <template #activator="{ props }">
            <div v-bind="props" class="d-flex ga-4 align-center px-4 py-1 rounded-xl cursor-pointer bg-grey">
              <div>
                <v-avatar class="primary-gradient" size="30">
                  <span class="text-white text-body-2 font-weight-bold">
                    {{ adminInitials }}
                  </span>
                </v-avatar>
              </div>
              <div class="d-flex flex-column">
                <span class="title"> {{ adminName }} </span>
                <span class="subtitle text-muted">{{ adminRole }}</span>
              </div>
              <div>
                <v-icon>mdi-menu-down</v-icon>
              </div>
            </div>
          </template>

          <v-list width="350" rounded="md" class="pa-0">
            <v-list-item class="my-2">
              <template #prepend>
                <v-avatar color="warning" size="40">
                  <span class="text-white text-body-2 font-weight-bold">
                    {{ adminInitials }}
                  </span>
                </v-avatar>
              </template>
              <template #append>
                <v-icon link>mdi-account-cog</v-icon>
              </template>
              <v-list-item-title class="title">{{ adminName?.toUpperCase() }}</v-list-item-title>
              <v-list-item-subtitle class="label">{{ adminRole?.toUpperCase() }}</v-list-item-subtitle>
            </v-list-item>

            <v-divider class="my-2" />
            <v-list-item class="ml-2 mr-2">
              <template #prepend><v-icon>mdi-email-outline</v-icon></template>
              <v-list-item-title class="label">{{ adminEmail }}</v-list-item-title>
            </v-list-item>

            <v-list-item class="ml-2 mr-2">
              <template #prepend><v-icon>mdi-phone-outline</v-icon></template>
              <v-list-item-title class="label">{{ adminPhone }}</v-list-item-title>
            </v-list-item>

            <v-btn append-icon="mdi-logout" block color="warning" rounded="0" class="text-caption mt-4 text-uppercase" @click="hanldeLogout">
              Log out
            </v-btn>
          </v-list>
        </v-menu>
      </v-col>
    </v-row>
  </v-app-bar>
</template>

<script>
import { mapActions } from "pinia";
import { useLoginStore } from "@/modules/auth/store/login";

export default {
  name: "TopBar",
  
  props: {
    drawer: {
      type: Boolean,
      default: null,
    },
  },

  data() {
    return {
      menu: false,
      notificationMenu: false,
      notifications: [
        {
          id: 1,
          title: "New booking received",
          message: "A new booking has been confirmed.",
          type: "booking",
          reference: "Booking #BK10245",
          time: "2 min ago",
          read: false,
          icon: "mdi-ticket-confirmation-outline",
        },

        {
          id: 2,
          title: "Trip schedule updated",
          message: "Departure time has been changed.",
          type: "trip",
          reference: "Trip #TRP1021",
          time: "18 min ago",
          read: false,
          icon: "mdi-bus-clock",
        },

        {
          id: 3,
          title: "Payment received",
          message: "Payment of ₹1,250 has been successfully received.",
          type: "payment",
          reference: "Payment #PAY4521",
          time: "35 min ago",
          read: true,
          icon: "mdi-cash-check",
        },

        {
          id: 4,
          title: "New customer registered",
          message: "A new customer has created an account.",
          type: "customer",
          reference: "Customer #CUS1023",
          time: "1 hour ago",
          read: true,
          icon: "mdi-account-plus-outline",
        },

        {
          id: 5,
          title: "Trip cancelled",
          message: "Chennai → Coimbatore trip was cancelled.",
          type: "trip",
          reference: "Trip #TRP1008",
          time: "2 hours ago",
          read: false,
          icon: "mdi-bus-alert",
        },
      ],
    };
  },

  computed: {
    adminInitials() {
      return this.$auth?.name ? this.$auth.name.slice(0, 2).toUpperCase() : "NA";
    },
    adminName() {
      return this.$auth?.name;
    },
    adminRole() {
      return this.$auth?.role;
    },
    adminEmail() {
      return this.$auth?.email || "No Email";
    },
    adminPhone() {
      return this.$auth?.phone_number || "No Phone";
    },
    unreadNotifications() {
      return this.notifications.filter(
        (notification) => !notification.read
      ).length;
    },
  },

  methods: {
    ...mapActions(useLoginStore, ["logout"]),

    hanldeLogout() {
      this.logout();
      this.$router.push("/login");
    },

    openNotification(notification) {
      notification.read = true;
      // You can navigate based on notification type here
      console.log("Notification clicked:", notification);
    },

    markAllAsRead() {
      this.notifications.forEach((notification) => {
        notification.read = true;
      });
    },

    viewAllNotifications() {
      this.notificationMenu = false;
      this.$router.push("/notifications");
    },
  },
};
</script>

<style scoped>
::v-deep(.v-list-item) {
  min-height: 36px !important;
}

/* Notification button */
.notification-btn {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 0.2s ease;
}

.notification-btn:hover {
  background: #f3f4f6;
}

/* Notification Card */
.notification-card {
  overflow: hidden;
}

/* List */
.notification-list {
  max-height: 390px;
  overflow-y: auto;
}

/* Item */
.notification-item {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  cursor: pointer;
  border-bottom: 1px solid #f0f1f3;
  transition: background 0.2s ease;
}

.notification-item:hover {
  background: #f8fafc;
}

.notification-unread {
  background: #f5f9ff;
}

/* Icon */
.notification-icon {
  width: 38px;
  height: 38px;
  min-width: 38px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.notification-icon.booking {
  background: #eef4ff;
  color: #3974e8;
}

.notification-icon.trip {
  background: #fff4e8;
  color: #e78a24;
}

.notification-icon.payment {
  background: #ecfdf3;
  color: #16a05d;
}

.notification-icon.customer {
  background: #f4f0ff;
  color: #7652d6;
}

/* Content */
.notification-content {
  flex: 1;
  min-width: 0;
}

.notification-content h4 {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: #344054;
}

.notification-content p {
  margin: 4px 0;
  color: #667085;
  font-size: 12px;
  line-height: 1.4;
}

.notification-time {
  color: #98a2b3;
  font-size: 10px;
  white-space: nowrap;
}

.notification-reference {
  color: #667085;
  font-size: 10px;
}

/* Unread */
.unread-dot {
  width: 7px;
  height: 7px;
  min-width: 7px;
  margin-top: 6px;
  border-radius: 50%;
  background: #3974e8;
}

/* Empty */
.notification-empty {
  padding: 45px 20px;
  text-align: center;
}

.notification-empty p {
  margin: 8px 0 0;
  color: #98a2b3;
  font-size: 13px;
}

/* Footer */
.notification-footer {
  padding: 8px;
}
</style>